import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { Sun, Battery, Zap, Thermometer, Wind, Car, ArrowRight, CheckCircle } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/home/CTASection';
import { services } from '../data/mock';

const iconMap = {
  Sun: Sun,
  Battery: Battery,
  Zap: Zap,
  Thermometer: Thermometer,
  Wind: Wind,
  Car: Car
};

// Real images for each service
const serviceImages = {
  'solar': 'https://images.pexels.com/photos/9875685/pexels-photo-9875685.jpeg',
  'battery': 'https://customer-assets.emergentagent.com/job_greenenergy-site/artifacts/r24ticpt_image.png',
  'solar-battery': 'https://images.pexels.com/photos/9875441/pexels-photo-9875441.jpeg',
  'heat-pump': 'https://customer-assets.emergentagent.com/job_greenenergy-site/artifacts/ygewb9qr_istore-270l-supplyinstall-lifetime-warrantywater-heaters-706985.jpg',
  'hvac': 'https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&q=85',
  'ev-charger': 'https://images.unsplash.com/photo-1694889651019-2d774dc5a9ac?crop=entropy&cs=srgb&fm=jpg&q=85'
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Energy Services — Solar, Battery, Heat Pump, HVAC, EV Charging"
        description="Complete energy solutions for Australian homes and businesses: solar PV, battery storage, heat pump hot water, energy-efficient HVAC, EV charging and electrification."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 hero-gradient">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-4">
                Our Services
              </span>
              <h1 className="heading-1 mb-6">
                Complete Home Energy Solutions
              </h1>
              <p className="body-large">
                From solar panels to EV chargers, we provide everything you need 
                to reduce your energy bills, eliminate gas dependency, and 
                future-proof your home.
              </p>
            </div>
          </div>
        </section>

        {/* Services Detail Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-24">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon];
                const isEven = index % 2 === 0;
                const imageUrl = serviceImages[service.id];
                
                return (
                  <div 
                    key={service.id}
                    id={service.id}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                      !isEven ? 'lg:flex-row-reverse' : ''
                    }`}
                  >
                    {/* Content */}
                    <div className={!isEven ? 'lg:order-2' : ''}>
                      <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mb-6">
                        <IconComponent className="w-7 h-7 text-[#3D5A47]" />
                      </div>
                      <h2 className="heading-2 mb-4">{service.title}</h2>
                      <p className="body-large mb-8">{service.description}</p>
                      
                      {/* Benefits */}
                      <ul className="space-y-3 mb-8">
                        {service.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                            <span className="text-[#3D5A47]">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Link 
                        to="/contact"
                        className="btn-primary inline-flex gap-2"
                      >
                        Get a Quote
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                    
                    {/* Image */}
                    <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                      <div className="relative rounded-3xl overflow-hidden shadow-xl">
                        <img 
                          src={imageUrl}
                          alt={service.title}
                          className="w-full aspect-[4/3] object-cover"
                        />
                        {/* Subtle overlay for consistency */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#3D5A47]/20 to-transparent" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
